import { changeRegisterForm, initialize } from 'modules/contractSlice';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { validateContractRegisterForm } from 'utils/validate';
import { useQueryClient, useMutation } from 'react-query';
import { createContract, updateContract } from 'lib/api/contract';
import { useNavigate } from 'react-router-dom';
import useLoginCheck from 'hooks/common/useLoginCheck';

const useContractCreator = edit => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const dispatch = useDispatch();

  const {
    contract: { company, code, termStartDate, termEndDate, name, tel, detail },
    store,
    user: {
      id,
      userId,
      password,
      passwordConfirm,
      name: username,
      team,
      position,
      tel: userTel,
      email,
    },
    originalContractId,
    updateStore,
    insertStore,
  } = useSelector(({ contract }) => contract.contractRegisterForm);
  const newTel = tel.replace(/-/g, '');
  const newUserTel = userTel.replace(/-/g, '');

  const { mutate, isLoading, isError, error, isSuccess, reset } =
    useMutation(createContract);
  useLoginCheck(error?.response, () => {
    mutate({
      contract: {
        company,
        code,
        termStartDate,
        termEndDate,
        name,
        tel: newTel,
        detail,
      },
      store,
      user: {
        userId,
        password,
        name: username,
        team,
        position,
        tel: newUserTel,
        email,
      },
    });
  });

  const updateContractMutation = useMutation(updateContract, {
    onSuccess: () => {
      queryClient.invalidateQueries('dashboardCounts');
      navigate('/admin/contracts');
    },
  });
  useLoginCheck(updateContractMutation.error?.response, () => {
    updateContractMutation.mutate({
      id: originalContractId,
      contract: {
        company,
        code,
        termStartDate,
        termEndDate,
        name,
        tel: newTel,
        detail,
      },
      updateStore,
      insertStore,
      user: {
        id,
        userId,
        name: username,
        team,
        position,
        tel: newUserTel,
        email,
      },
    });
  });

  const [errors, setErrors] = useState({});
  const [isSubmit, setIsSubmit] = useState(false);
  const [isEdit, setIsEdit] = useState(false);

  useEffect(() => {
    return () => dispatch(initialize());
  }, [dispatch]);

  useEffect(() => {
    if (isSubmit && Object.keys(errors).length === 0) {
      setIsSubmit(false);
      mutate({
        contract: {
          company,
          code,
          termStartDate,
          termEndDate,
          name,
          tel: newTel,
          detail,
        },
        store,
        user: {
          userId,
          password,
          name: username,
          team,
          position,
          tel: newUserTel,
          email,
        },
      });
    }
  }, [
    mutate,
    isSubmit,
    errors,
    company,
    code,
    termStartDate,
    termEndDate,
    name,
    newTel,
    detail,
    store,
    id,
    userId,
    password,
    username,
    team,
    position,
    newUserTel,
    email,
  ]);

  useEffect(() => {
    if (isError) {
      if (error.response.data.error === 'AlreadyContractCompanyExist') {
        setErrors({ ...errors, company: error.response.data.msg });
      }
      if (error.response.data.error === 'AlreadyContractCodeExist') {
        setErrors({
          ...errors,
          code: error.response.data.msg,
        });
      }
      if (error.response.data.error === 'AlreadyStoreExist') {
        setErrors({
          ...errors,
          store: error.response.data.msg,
        });
      }
      if (error.response.data.error === 'AlreadyUserExist') {
        setErrors({ ...errors, userId: error.response.data.msg });
      }
      reset();
      setIsSubmit(false);
    }
    if (isSuccess) {
      queryClient.invalidateQueries('dashboardCounts');
      navigate('/admin/contracts');
    }
  }, [isError, error, errors, reset, isSuccess, navigate, queryClient]);

  useEffect(() => {
    if (isEdit && Object.keys(errors).length === 0) {
      setIsEdit(false);
      updateContractMutation.mutate({
        id: originalContractId,
        contract: {
          company,
          code,
          termStartDate,
          termEndDate,
          name,
          tel: newTel,
          detail,
        },
        updateStore,
        insertStore,
        user: {
          id,
          userId,
          name: username,
          team,
          position,
          tel: newUserTel,
          email,
        },
      });
    }
  }, [
    isEdit,
    errors,
    updateContractMutation,
    originalContractId,
    company,
    code,
    termStartDate,
    termEndDate,
    name,
    newTel,
    detail,
    updateStore,
    insertStore,
    id,
    userId,
    username,
    team,
    position,
    newUserTel,
    email,
  ]);

  const onEdit = () => {
    setErrors(
      validateContractRegisterForm({
        edit,
        company,
        code,
        name,
        tel,
        userId,
        username,
        team,
        position,
        userTel,
        email,
      }),
    );
    setIsEdit(true);
  };

  const onSubmit = () => {
    setErrors(
      validateContractRegisterForm({
        company,
        code,
        name,
        tel,
        userId,
        password,
        passwordConfirm,
        username,
        team,
        position,
        userTel,
        email,
      }),
    );
    setIsSubmit(true);
  };

  const onChange = (e, form) => {
    const { name, value } = e.target;
    dispatch(changeRegisterForm({ form, key: name, value }));
  };

  return {
    company,
    code,
    name,
    tel,
    detail,
    userId,
    password,
    passwordConfirm,
    username,
    team,
    position,
    userTel,
    email,
    errors,
    isLoading,
    isError,
    error,
    updateContractMutation,
    onChange,
    onSubmit,
    onEdit,
  };
};

export default useContractCreator;
